/**
 * TaskRow Component
 * Single task row used in both the Task Scheduler and My Tasks pages.
 * Renders different layouts depending on the `variant` prop.
 */

import { Pin } from 'lucide-react';
import styled from 'styled-components';
import type { TaskStatus } from '../../types/tasks';
import { StatusBadge } from './StatusBadge';
import { SLAPill } from './SLAPill';
import { WorkflowTypeBadge } from './WorkflowTypeBadge';

// ============================================================================
// Types
// ============================================================================

interface TaskRowTask {
  id: string;
  name: string;
  status: TaskStatus;
  assignedTo: { firstName: string; lastName: string } | null;
  slaContext: { daysRemaining: number; slaBand: string } | null;
  dueDate: string | null;
  pinned: boolean;
  workflowInstance?: {
    workflowType: string;
    id: string;
    clientMember?: { firstName: string; lastName: string } | null;
  } | null;
}

export interface TaskRowProps {
  task: TaskRowTask;
  variant: 'scheduler' | 'myTasks';
  onViewDetails?: (taskId: string) => void;
  showWorkflowBadge?: boolean;
  showWorkflowRef?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

function formatDate(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fullName(user: { firstName: string; lastName: string } | null): string {
  return user ? `${user.firstName} ${user.lastName}` : 'Unassigned';
}

function isValidSLABand(band: string): band is 'green' | 'yellow' | 'red' {
  return band === 'green' || band === 'yellow' || band === 'red';
}

// ============================================================================
// Styled components
// ============================================================================

const Row = styled.article`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  transition: background ${({ theme }) => theme.transitions.fast};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }
`;

const LeftSection = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  overflow: hidden;
`;

const TaskInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TaskName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-shrink: 0;
`;

const DateText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  white-space: nowrap;
`;

const ViewDetailsLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.primary.base};
  cursor: pointer;
  white-space: nowrap;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primary.dark};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
`;

const PinIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.accent.gold};
  flex-shrink: 0;
`;

const AssigneeText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const WorkflowRef = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  white-space: nowrap;
  font-family: monospace;
`;

// ============================================================================
// Component
// ============================================================================

export function TaskRow({
  task,
  variant,
  onViewDetails,
  showWorkflowBadge = true,
  showWorkflowRef = false,
}: TaskRowProps) {
  const sla =
    task.slaContext && isValidSLABand(task.slaContext.slaBand)
      ? { daysRemaining: task.slaContext.daysRemaining, slaBand: task.slaContext.slaBand as 'green' | 'yellow' | 'red' }
      : null;

  const formattedDate = formatDate(task.dueDate);

  if (variant === 'scheduler') {
    return (
      <Row aria-label={`Task: ${task.name}`}>
        <LeftSection>
          <TaskInfo>
            <TaskName title={task.name}>{task.name}</TaskName>
            <SubText>{fullName(task.assignedTo)}</SubText>
          </TaskInfo>
        </LeftSection>
        <RightSection>
          <StatusBadge status={task.status} size="sm" />
          {sla && <SLAPill daysRemaining={sla.daysRemaining} slaBand={sla.slaBand} compact />}
          {formattedDate && <DateText>{formattedDate}</DateText>}
          {onViewDetails && (
            <ViewDetailsLink
              onClick={() => onViewDetails(task.id)}
              aria-label={`View details for ${task.name}`}
              type="button"
            >
              View Details &rsaquo;
            </ViewDetailsLink>
          )}
        </RightSection>
      </Row>
    );
  }

  // myTasks variant
  return (
    <Row aria-label={`Task: ${task.name}`}>
      <LeftSection>
        {showWorkflowBadge && task.workflowInstance && (
          <WorkflowTypeBadge workflowType={task.workflowInstance.workflowType} />
        )}
        <TaskName title={task.name}>{task.name}</TaskName>
        {task.pinned && (
          <PinIcon aria-label="Pinned task">
            <Pin size={12} strokeWidth={2.5} />
          </PinIcon>
        )}
      </LeftSection>
      <RightSection>
        <AssigneeText>{fullName(task.assignedTo)}</AssigneeText>
        {showWorkflowRef && task.workflowInstance && (
          <WorkflowRef title={task.workflowInstance.id}>
            #{task.workflowInstance.id.slice(0, 8)}
          </WorkflowRef>
        )}
        {sla && <SLAPill daysRemaining={sla.daysRemaining} slaBand={sla.slaBand} compact />}
        <StatusBadge status={task.status} size="sm" />
        {formattedDate && <DateText>{formattedDate}</DateText>}
        {onViewDetails && (
          <ViewDetailsLink
            onClick={() => onViewDetails(task.id)}
            aria-label={`View details for ${task.name}`}
            type="button"
          >
            View Details &rsaquo;
          </ViewDetailsLink>
        )}
      </RightSection>
    </Row>
  );
}

export default TaskRow;
